// Даты поля «дд.мм.гггг» (README «Тексты»): разбор и маска ввода, календарная арифметика без
// часового пояса. Значение — ISO «2026-10-20», как у сервера и в адресе страницы. Названия
// месяцев и дней — свои, по-русски: не зависят от языка браузера.

/** Названия месяцев для заголовка календаря: «Октябрь 2026». */
export const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
] as const;

/** Месяц в дате: «20 октября 2026». */
export const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const;

/** Дни недели с понедельника: подписи колонок календаря и их полные названия. */
export const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;
export const WEEKDAYS_FULL = [
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
  'воскресенье',
] as const;

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const TEXT = /^(\d{2})\.(\d{2})\.(\d{4})$/;
/** Чем человек разделяет части даты: точка, запятая, косая черта, дефис, пробел. */
const SEPARATOR = /[.,/\\\- ]/;

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Есть ли такой день в календаре: 31.02 — нет. */
function isRealDate(year: number, month: number, day: number): boolean {
  return year >= 1000 && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

/** «2026-10-20» → «20.10.2026»; пусто и не дата — ''. */
export function isoToDateText(iso: string | null | undefined): string {
  const match = ISO.exec(iso ?? '');
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '';
}

/** «20.10.2026» → «2026-10-20»; неполная или несуществующая дата — null. */
export function parseDateText(text: string): string | null {
  const match = TEXT.exec(text.trim());
  if (!match) return null;
  const [, day = '', month = '', year = ''] = match;
  if (!isRealDate(Number(year), Number(month), Number(day))) return null;
  return `${year}-${month}-${day}`;
}

/**
 * Маска ввода «дд.мм.гггг»: точки встают сами, после одной цифры и разделителя день и месяц
 * дополняются нулём («5.» → «05.»), лишние знаки отбрасываются. Вставленная ISO-дата
 * («2026-10-20») переводится в «20.10.2026».
 */
export function maskDateText(raw: string): string {
  const iso = ISO.exec(raw.trim());
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const parts: string[] = [''];
  for (const char of raw) {
    const index = parts.length - 1;
    const part = parts[index] ?? '';
    if (char >= '0' && char <= '9') {
      if (index < 2 && part.length === 2) parts.push(char);
      else if (part.length < (index === 2 ? 4 : 2)) parts[index] = part + char;
    } else if (SEPARATOR.test(char) && index < 2 && part.length > 0) {
      parts[index] = part.padStart(2, '0');
      parts.push('');
    }
  }
  return parts.join('.');
}

/** Место каретки после маски: столько же цифр слева, сколько было до неё в исходном вводе. */
export function caretAfterMask(masked: string, digitsBefore: number): number {
  if (digitsBefore <= 0) return 0;
  let digits = 0;
  for (let index = 0; index < masked.length; index += 1) {
    const char = masked[index] ?? '';
    if (char >= '0' && char <= '9') digits += 1;
    if (digits === digitsBefore) {
      // После целой части — за точкой: следующая цифра пойдёт в следующую часть.
      return masked[index + 1] === '.' ? index + 2 : index + 1;
    }
  }
  return masked.length;
}

/** Сегодня по часам браузера: «2026-10-20». Сервис передаёт сегодня по поясу объекта сам. */
export function localToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function toParts(iso: string): { year: number; month: number; day: number } {
  const [year = 1970, month = 1, day = 1] = iso.split('-').map(Number);
  return { year, month, day };
}

function fromUtc(moment: Date): string {
  return moment.toISOString().slice(0, 10);
}

/** Сдвиг даты на N дней. */
export function addDays(iso: string, days: number): string {
  const { year, month, day } = toParts(iso);
  return fromUtc(new Date(Date.UTC(year, month - 1, day + days)));
}

/** Сдвиг на N месяцев; день — не больше последнего дня нового месяца (31.01 + 1 → 28.02). */
export function addMonths(iso: string, months: number): string {
  const { year, month, day } = toParts(iso);
  const first = new Date(Date.UTC(year, month - 1 + months, 1));
  const last = daysInMonth(first.getUTCFullYear(), first.getUTCMonth() + 1);
  first.setUTCDate(Math.min(day, last));
  return fromUtc(first);
}

/** День недели с понедельника: 0 — понедельник, 6 — воскресенье. */
export function weekdayOf(iso: string): number {
  const { year, month, day } = toParts(iso);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

/** Первый день месяца даты: «2026-10-20» → «2026-10-01». */
export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Недели месяца с понедельника; дни соседних месяцев — null. */
export function monthWeeks(iso: string): (string | null)[][] {
  const start = monthStart(iso);
  const { year, month } = toParts(start);
  const total = daysInMonth(year, month);
  const cells: (string | null)[] = Array.from({ length: weekdayOf(start) }, () => null);
  for (let day = 1; day <= total; day += 1) cells.push(`${year}-${pad(month)}-${pad(day)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let index = 0; index < cells.length; index += 7) weeks.push(cells.slice(index, index + 7));
  return weeks;
}

/** Заголовок календаря: «Октябрь 2026». */
export function monthTitle(iso: string): string {
  const { year, month } = toParts(iso);
  return `${MONTHS[month - 1] ?? ''} ${year}`;
}

/** Дата словами для программ чтения: «20 октября 2026, вторник». */
export function spokenDate(iso: string): string {
  const { year, month, day } = toParts(iso);
  return `${day} ${MONTHS_GENITIVE[month - 1] ?? ''} ${year}, ${WEEKDAYS_FULL[weekdayOf(iso)] ?? ''}`;
}

/** Дата в пределах min–max (ISO-строки сравниваются как даты). */
export function clampDate(iso: string, min?: string, max?: string): string {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}

export function isInRange(iso: string, min?: string, max?: string): boolean {
  return (!min || iso >= min) && (!max || iso <= max);
}
